/**
 * Süper Admin → Seçici Kurtarma (Products + Product Variants only)
 * 29/08 yedeğinden sadece ürün ve varyantları duplicate-safe geri yükler.
 * Adım 1: Yedek yükle → Adım 2: Önizleme → Adım 3: Kurtarma başlat.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldAlert, Upload, PlayCircle, Rocket, RefreshCw, CheckCircle2,
  XCircle, AlertTriangle, Loader2, Package, Copy,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const COLL_LABELS = { products: "Ürün", product_variants: "Ürün Varyantı" };

export default function SelectiveRestore() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (user && !user.is_super_admin) navigate("/");
  }, [user, navigate]);

  const loadStatus = async () => {
    try {
      const r = await api.get("/super-admin/selective-restore/status");
      setState(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Durum yüklenemedi");
    }
  };
  useEffect(() => { loadStatus(); }, []);

  const handleUpload = async (evt) => {
    const file = evt.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".tar.gz") && !file.name.toLowerCase().endsWith(".tgz")) {
      toast.error("Sadece .tar.gz dosyaları kabul edilir");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setBusy("upload");
    try {
      const r = await api.post("/super-admin/selective-restore/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setState(r.data);
      toast.success(`Yedek yüklendi (${r.data.collections_found} koleksiyon bulundu)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Yükleme başarısız");
    } finally {
      setBusy(null);
      evt.target.value = "";
    }
  };

  const handlePreview = async () => {
    setBusy("preview");
    try {
      const r = await api.post("/super-admin/selective-restore/preview");
      setState(r.data);
      const t = r.data.preview_report?.totals;
      toast.success(`Önizleme: ${t?.would_insert || 0} kayıt eklenecek, ${t?.already_in_prod || 0} mevcut (atlanır)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Önizleme başarısız");
    } finally {
      setBusy(null);
    }
  };

  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const runDetail = async () => {
    setDetailLoading(true);
    try {
      const r = await api.post("/super-admin/selective-restore/preview/detail");
      setDetailData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Detaylı analiz başarısız");
    } finally { setDetailLoading(false); }
  };

  const handleExecute = async () => {
    setBusy("execute");
    setConfirmOpen(false);
    try {
      const r = await api.post("/super-admin/selective-restore/execute");
      setState(r.data);
      if (r.data.execute_status === "success") toast.success("Kurtarma başarılı");
      else toast.error("Kurtarma DURDU — raporu inceleyin");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kurtarma başarısız");
    } finally {
      setBusy(null);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Oturumu sıfırla?")) return;
    setBusy("reset");
    try {
      const r = await api.post("/super-admin/selective-restore/reset");
      setState(r.data);
      toast.success("Oturum sıfırlandı");
    } catch (e) { toast.error(e.message); }
    finally { setBusy(null); }
  };

  const pr = state?.preview_report;
  const ex = state?.execute_report;
  const totals = pr?.totals;

  return (
    <div className="space-y-6" data-testid="selective-restore-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-100 tracking-tight flex items-center gap-3">
            <Package className="w-7 h-7 text-blue-500" />
            Seçici Kurtarma — Ürün + Varyant
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            29/08 yedeğinden yalnızca <span className="font-mono text-blue-400">products</span>
            {" "}ve <span className="font-mono text-blue-400">product_variants</span> kayıtlarını
            duplicate-safe geri yükler. <span className="text-slate-300">Diğer tüm koleksiyonlar dokunulmaz.</span>
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={handleReset}
                disabled={busy !== null || (!state?.session_id && !state?.executed_at)}
                data-testid="selective-reset-btn"
                className="rounded-sm text-xs uppercase tracking-wider">
          <RefreshCw className="w-4 h-4 mr-2" /> Sıfırla
        </Button>
      </div>

      <div className="rounded-sm border border-amber-800/60 bg-amber-950/30 p-3 text-xs text-amber-200 flex gap-2">
        <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <div>
          <strong>Güvenlik:</strong> Yalnızca <em>products</em> ve <em>product_variants</em> koleksiyonları işlenir.
          Aynı <code className="text-amber-100">id</code>'ye sahip kayıtlar için ekleme yapılmaz (mükerrer önleme).
          Insert-only — hiçbir kayıt güncellenmez veya silinmez.
          Müşteriler, siparişler, stok, muhasebe, PDKS, sevkiyat vb. <strong>hiç dokunulmaz</strong>.
        </div>
      </div>

      {/* Step 1: Upload */}
      <StepCard number={1} title="Yedek Dosyasını Yükle" active={!state?.session_id} done={!!state?.session_id}>
        <p className="text-xs text-slate-400">29/08/2026 13:00 manuel yedeğini indirdikten sonra .tar.gz olarak buraya yükleyin.</p>
        <div className="flex items-center gap-3">
          <label className="cursor-pointer">
            <input type="file" accept=".tar.gz,.tgz,application/gzip,application/x-gzip"
                   className="hidden" onChange={handleUpload}
                   disabled={busy !== null || state?.executed_at}
                   data-testid="selective-upload-input" />
            <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-sm text-xs uppercase tracking-wider font-semibold border transition-colors ${
              busy || state?.executed_at
                ? "bg-slate-800 border-slate-700 text-slate-500 cursor-not-allowed"
                : "bg-blue-600/20 border-blue-700 text-blue-300 hover:bg-blue-600/30"
            }`}>
              {busy === "upload" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Yedek Dosyası Seç
            </span>
          </label>
          {state?.session_id && (
            <div className="text-xs text-slate-400">
              Oturum: <span className="font-mono text-slate-300">{state.session_id.slice(0, 8)}</span> ·
              <span className="ml-2 text-emerald-400">{state.collections_found} koleksiyon</span>
            </div>
          )}
        </div>
      </StepCard>

      {/* Step 2: Preview */}
      <StepCard number={2} title="Önizleme (Yazma Yok)" active={state?.can_preview && !state?.preview_status}
                done={state?.preview_status === "ready"}>
        <p className="text-xs text-slate-400">Yedekteki ürün+varyant sayısını ve kaçının zaten Production'da olduğunu hesaplar.</p>
        <Button onClick={handlePreview} disabled={!state?.can_preview || busy !== null}
                data-testid="selective-preview-btn"
                className="bg-slate-700 hover:bg-slate-600 rounded-sm uppercase tracking-wider text-xs font-semibold">
          {busy === "preview" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-2" />}
          Önizleme Çalıştır
        </Button>

        {pr && (
          <div className="mt-3 space-y-3" data-testid="selective-preview-report">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <StatBox label="Yedekte" val={totals?.backup_total ?? 0} testId="prev-backup-total" />
              <StatBox label="Prod'da Mevcut (atlanır)" val={totals?.already_in_prod ?? 0}
                       color="slate" testId="prev-existing-total" />
              <StatBox label="Insert Edilecek" val={totals?.would_insert ?? 0}
                       color="emerald" testId="prev-would-insert" />
            </div>
            <PerCollectionTable per={pr.per_collection} />
            <div>
              <Button onClick={runDetail} disabled={detailLoading}
                      data-testid="selective-detail-btn"
                      className="bg-slate-800 hover:bg-slate-700 rounded-sm uppercase tracking-wider text-xs">
                {detailLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : "Detaylı Karşılaştırma (Kayıt Bazlı)"}
              </Button>
              {detailData && <DetailReport data={detailData} />}
            </div>
          </div>
        )}
      </StepCard>

      {/* Step 3: Execute */}
      <StepCard number={3} title="Seçici Kurtarma" active={state?.can_execute}
                done={state?.execute_status === "success"} failed={state?.execute_status === "failed"}>
        <div className="rounded-sm border border-amber-800/60 bg-amber-950/30 p-3 text-xs text-amber-200 flex gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>Bu buton yalnızca başarılı Önizleme sonrasında aktifleşir. Kurtarma öncesi ürün ve varyant koleksiyonlarının JSON yedeği alınır.</div>
        </div>
        <Button onClick={() => setConfirmOpen(true)} disabled={!state?.can_execute || busy !== null}
                data-testid="selective-execute-btn"
                className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold">
          {busy === "execute" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Rocket className="w-4 h-4 mr-2" />}
          Seçici Kurtarmayı Başlat
        </Button>

        {ex && <ExecuteReport ex={ex} />}
      </StepCard>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="bg-slate-900 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-100">Kurtarmayı başlatalım mı?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              <span className="font-mono text-blue-300">{totals?.would_insert ?? 0}</span> yeni ürün+varyant kaydı Prod'a eklenecek.
              <br />
              <span className="font-mono text-slate-300">{totals?.already_in_prod ?? 0}</span> mevcut kayıt atlanacak.
              <br />
              Diğer koleksiyonlara <strong>dokunulmayacak</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">İptal</AlertDialogCancel>
            <AlertDialogAction onClick={handleExecute}
                               className="bg-blue-600 hover:bg-blue-500 rounded-sm"
                               data-testid="selective-execute-confirm">
              Evet, Kurtar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StepCard({ number, title, active, done, failed, children }) {
  const stateColor = done ? "border-emerald-800/60" : failed ? "border-rose-800/60"
    : active ? "border-blue-800/60" : "border-slate-800";
  const numColor = done ? "bg-emerald-600 text-white" : failed ? "bg-rose-600 text-white"
    : active ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-400";
  return (
    <Card className={`bg-slate-900/50 border ${stateColor} rounded-sm`} data-testid={`selective-step-${number}`}>
      <CardHeader className="border-b border-slate-800 py-3 flex-row items-center gap-3">
        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${numColor}`}>
          {done ? <CheckCircle2 className="w-4 h-4" /> : failed ? <XCircle className="w-4 h-4" /> : number}
        </div>
        <CardTitle className="text-slate-100 uppercase text-xs tracking-widest font-semibold flex-1">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 space-y-3">{children}</CardContent>
    </Card>
  );
}

function StatBox({ label, val, color = "orange", testId }) {
  const colorMap = { orange: "text-blue-300", slate: "text-slate-300", emerald: "text-emerald-300" };
  return (
    <div className="rounded-sm border border-slate-800 bg-slate-900/50 p-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{label}</div>
      <div className={`text-2xl font-bold font-mono ${colorMap[color]}`} data-testid={testId}>{val}</div>
    </div>
  );
}

function PerCollectionTable({ per }) {
  if (!per) return null;
  return (
    <div className="rounded-sm border border-slate-800 overflow-hidden">
      <table className="w-full text-xs">
        <thead className="bg-slate-900/60">
          <tr>
            <th className="text-left px-2 py-1 text-slate-400 font-mono uppercase text-[10px]">Koleksiyon</th>
            <th className="text-right px-2 py-1 text-slate-400 font-mono uppercase text-[10px]">Yedekte</th>
            <th className="text-right px-2 py-1 text-slate-400 font-mono uppercase text-[10px]">Prod'da</th>
            <th className="text-right px-2 py-1 text-slate-400 font-mono uppercase text-[10px]">28/08</th>
            <th className="text-right px-2 py-1 text-slate-400 font-mono uppercase text-[10px]">Eklenecek</th>
            <th className="text-right px-2 py-1 text-slate-400 font-mono uppercase text-[10px]">Yetim</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(per).map(([coll, info]) => (
            <tr key={coll} className="border-t border-slate-800">
              <td className="px-2 py-1 text-slate-100 font-mono">{COLL_LABELS[coll] || coll}</td>
              <td className="px-2 py-1 text-right text-slate-300 font-mono">{info.backup_count}</td>
              <td className="px-2 py-1 text-right text-slate-500 font-mono">{info.already_in_prod}</td>
              <td className="px-2 py-1 text-right text-blue-300 font-mono">{info.aug28_in_backup}</td>
              <td className="px-2 py-1 text-right text-emerald-300 font-mono font-bold">{info.would_insert}</td>
              <td className="px-2 py-1 text-right text-rose-300 font-mono">{info.orphan_variants ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ExecuteReport({ ex }) {
  const post = ex?.post_counts || {};
  return (
    <div className="mt-3 space-y-3" data-testid="selective-execute-report">
      {ex.status === "SUCCESS" ? (
        <div className="rounded-sm border border-emerald-800/60 bg-emerald-950/30 p-3 text-xs text-emerald-200 flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5" /> Kurtarma başarılı — {new Date(ex.finished_at).toLocaleString("tr-TR")}
        </div>
      ) : (
        <div className="rounded-sm border border-rose-800/60 bg-rose-950/30 p-3 text-xs text-rose-200">
          <div className="font-semibold flex items-center gap-2"><XCircle className="w-4 h-4" /> {ex.status}</div>
          <div className="mt-1">{ex.halt_reason}</div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        {Object.entries(post).map(([coll, cnt]) => (
          <div key={coll} className="rounded-sm border border-slate-800 bg-slate-900/50 p-2">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{COLL_LABELS[coll] || coll} (Prod)</div>
            <div className="text-lg font-bold text-emerald-300 font-mono">{cnt}</div>
          </div>
        ))}
      </div>
      <div className="rounded-sm border border-slate-800 overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-slate-900/60">
            <tr>
              <th className="text-left px-2 py-1 text-slate-400 uppercase text-[10px]">Koleksiyon</th>
              <th className="text-right px-2 py-1 text-slate-400 uppercase text-[10px]">Denendi</th>
              <th className="text-right px-2 py-1 text-slate-400 uppercase text-[10px]">Eklendi</th>
              <th className="text-right px-2 py-1 text-slate-400 uppercase text-[10px]">Atlandı</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(ex.per_collection || {}).map(([coll, info]) => (
              <tr key={coll} className="border-t border-slate-800">
                <td className="px-2 py-1 text-slate-100 font-mono">{COLL_LABELS[coll] || coll}</td>
                <td className="px-2 py-1 text-right text-slate-300 font-mono">{info.attempted || 0}</td>
                <td className="px-2 py-1 text-right text-emerald-300 font-mono font-bold">{info.inserted || 0}</td>
                <td className="px-2 py-1 text-right text-slate-500 font-mono">{info.skipped_duplicate || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {ex.pre_backup?.path && (
        <div className="text-[10px] text-slate-500 font-mono">Pre-restore yedek: {ex.pre_backup.path}</div>
      )}
    </div>
  );
}


function DetailReport({ data }) {
  if (!data) return null;
  const p = data.products || {};
  const v = data.variants || {};
  return (
    <div className="mt-3 space-y-3" data-testid="selective-detail-report">
      <div className="rounded-sm border border-slate-800 bg-slate-900/50 p-3 text-xs text-slate-300">
        <div className="font-semibold text-slate-100 mb-2">PRODUCTS (backup: {p.backup_count})</div>
        <div>Exact ID: <span className="font-mono text-emerald-300">{p.exact_id_matches}</span> ·
          SKU-only: <span className="font-mono text-amber-300">{p.sku_only_matches}</span> ·
          Missing: <span className="font-mono text-rose-300">{p.missing}</span> ·
          28/08: <span className="font-mono text-blue-300">{p.aug28_in_backup}</span></div>
      </div>
      <div className="rounded-sm border border-slate-800 overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-900/60"><tr>
            <th className="text-left px-2 py-1 text-slate-400 uppercase text-[10px]">Backup Adı</th>
            <th className="text-left px-2 py-1 text-slate-400 uppercase text-[10px]">SKU</th>
            <th className="text-left px-2 py-1 text-slate-400 uppercase text-[10px]">Backup Tarih</th>
            <th className="text-left px-2 py-1 text-slate-400 uppercase text-[10px]">Prod Adı</th>
            <th className="text-left px-2 py-1 text-slate-400 uppercase text-[10px]">Eşleşme</th>
          </tr></thead>
          <tbody>
            {(p.rows || []).map((r, i) => (
              <tr key={i} className="border-t border-slate-800">
                <td className="px-2 py-1 text-slate-100">{r.backup_name || "—"}</td>
                <td className="px-2 py-1 font-mono text-slate-400">{r.backup_sku || "—"}</td>
                <td className="px-2 py-1 font-mono text-[10px] text-slate-500">{(r.backup_created_at || "").slice(0, 19)}</td>
                <td className="px-2 py-1 text-slate-200">{r.prod_matched_name || "—"}</td>
                <td className="px-2 py-1">
                  {r.exact_id_match ? <span className="text-emerald-300">Exact ID</span> :
                   r.sku_only_match ? <span className="text-amber-300">SKU only</span> :
                   <span className="text-rose-300">Missing</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="rounded-sm border border-slate-800 bg-slate-900/50 p-3 text-xs text-slate-300">
        <div className="font-semibold text-slate-100 mb-2">VARIANTS (backup: {v.backup_count})</div>
        <div>Exact ID: <span className="font-mono text-emerald-300">{v.exact_id_matches}</span> ·
          SKU-only: <span className="font-mono text-amber-300">{v.sku_only_matches}</span> ·
          Missing: <span className="font-mono text-rose-300">{v.missing}</span> ·
          28/08: <span className="font-mono text-blue-300">{v.aug28_in_backup}</span></div>
      </div>
      <div className="rounded-sm border border-slate-800 overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-slate-900/60"><tr>
            <th className="text-left px-2 py-1 text-slate-400 uppercase text-[10px]">Ürün</th>
            <th className="text-right px-2 py-1 text-slate-400 uppercase text-[10px]">Backup Var.</th>
            <th className="text-right px-2 py-1 text-slate-400 uppercase text-[10px]">Exact Eşleşen</th>
          </tr></thead>
          <tbody>
            {Object.entries(data.by_product || {}).map(([pid, info]) => (
              <tr key={pid} className="border-t border-slate-800">
                <td className="px-2 py-1 text-slate-100">{info.product_name}</td>
                <td className="px-2 py-1 text-right font-mono text-slate-300">{info.backup_variants}</td>
                <td className="px-2 py-1 text-right font-mono text-emerald-300">{info.exact_variant_matches}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
